import React, { useMemo, useRef, type ReactElement } from 'react';
import { RefreshControl, StyleSheet, View } from 'react-native';

import Colors from '@constants/Colors';
import { CardListDateHeader } from '@molecules/CardListDateHeader';
import InPersonEventCard from '@molecules/InPersonEventCard';
import type { ViewToken } from '@shopify/flash-list';
import { FlashList, MasonryFlashList } from '@shopify/flash-list';
import {
  QueryKeys,
  useBulkGetCommunities,
  useBulkGetParentCommunityIds,
  useGetTicketCountForEvents,
} from '@sunrise-ui/api-client';
import { type Event } from '@sunrise-ui/api/events';
import {
  CARD_MARGIN,
  CacheAvailabilityProvider,
  Loader,
  TextReg,
  useDynamicStyles,
  useMediaQueries,
} from '@sunrise-ui/primitives';
import type { FlashListItem } from '@utils/eventList';
import {
  createFlashListData,
  createMarkedDates,
  groupEventsByDateForFlashList,
  scrollToFlashListDate,
} from '@utils/eventList';
import { DateTime } from 'luxon';

type Props = {
  events?: Event[];
  isLoading: boolean;
  emptyStateText?: string;
  onRefresh: () => Promise<void>;
  ListHeaderComponent?: ReactElement;
  sortDirection?: 'asc' | 'desc';
};

const { communitiesKey, byIdKey, parentsKey } = QueryKeys.communities;

export const EventCardsList = ({
  events,
  isLoading,
  emptyStateText,
  onRefresh,
  ListHeaderComponent,
  sortDirection = 'asc',
}: Props) => {
  const { isMobile } = useMediaQueries();
  const styles = useDynamicStyles(createStyles);
  const flashListRef = useRef<FlashList<FlashListItem>>(null);
  const viewableItemIndicesRef = useRef<number[]>([]);

  const groupedEvents = useMemo(
    () => groupEventsByDateForFlashList(events, sortDirection),
    [events, sortDirection],
  );

  const flashListData = useMemo(
    () => createFlashListData(groupedEvents),
    [groupedEvents],
  );

  const markedDates = useMemo(() => createMarkedDates(events), [events]);

  // Extract unique community IDs from events for bulk fetch
  const eventCommunityIds = useMemo(() => {
    const ids = events
      ?.map((event) => event.community_id)
      .filter((id): id is string => Boolean(id));
    return [...new Set(ids)];
  }, [events]);

  const {
    data: bulkParentCommunityIdsData,
    isLoading: isLoadingParentCommunityIds,
  } = useBulkGetParentCommunityIds({
    communityIds: eventCommunityIds,
    enabled: !isLoading,
  });

  // Extract all community IDs (event communities + their parents)
  const allCommunityIds = useMemo(() => {
    const ids = [...eventCommunityIds];
    if (bulkParentCommunityIdsData) {
      Object.values(bulkParentCommunityIdsData).forEach((parentIds) => {
        ids.push(...parentIds);
      });
    }
    return Array.from(new Set(ids));
  }, [bulkParentCommunityIdsData, eventCommunityIds]);

  // Bulk fetch community profiles
  useBulkGetCommunities({
    communityIds: allCommunityIds,
    enabled: !isLoading && !isLoadingParentCommunityIds,
  });

  // Extract event IDs that need ticket counts (exclude external ticketing)
  const eventIdsForTicketCount = useMemo(() => {
    return events
      ?.filter((event) => !event.ticketing_platform_url)
      .map((event) => event.id)
      .filter((id): id is string => Boolean(id));
  }, [events]);

  // Bulk fetch ticket counts - populates individual caches for InPersonEventCard
  useGetTicketCountForEvents(
    eventIdsForTicketCount && eventIdsForTicketCount.length > 0
      ? eventIdsForTicketCount
      : undefined,
  );

  const handleDateSelect = (selectedDate: string) => {
    scrollToFlashListDate({
      selectedDate,
      flashListRef,
      groupedEvents,
      viewableItemIndicesRef,
    });
  };

  const handleViewableItemsChanged = ({
    viewableItems,
  }: {
    viewableItems: ViewToken[];
  }) => {
    viewableItemIndicesRef.current = viewableItems
      .map((item) => item.index)
      .filter((item) => item !== null) as number[];
  };

  const lastDate =
    DateTime.fromISO(
      groupedEvents[groupedEvents.length - 1]?.events[0].start_date ?? '',
    ).toISODate() ?? undefined;

  const stickyHeaderIndices = flashListData
    .filter((item) => item.type === 'header')
    .map((item) => item.sectionIndex * 2);

  const renderItem = ({ item }: { item: FlashListItem }) => {
    if (item.type === 'header') {
      return (
        <CardListDateHeader
          startDateTime={item.startDateTime}
          onDateSelect={handleDateSelect}
          markedDates={markedDates}
          minDate={
            sortDirection === 'asc'
              ? (DateTime.now().toISODate() ?? undefined)
              : lastDate
          }
          hideTime={true}
        />
      );
    }

    const isOneColumn = isMobile || item.events?.length === 1;

    return (
      <View style={styles.nestedFlashListWrapper}>
        <View
          style={
            isOneColumn
              ? styles.oneColumnListWrapper
              : styles.twoColumnListWrapper
          }
        >
          <MasonryFlashList
            data={item.events}
            renderItem={({ item: event }) => (
              <InPersonEventCard
                key={event.id}
                style={styles.card}
                eventId={event.id!}
                title={event.name}
                locationId={event.event_location_id}
                startDate={DateTime.fromISO(event.start_date!)}
                endDate={
                  event.scheduled_end_date
                    ? DateTime.fromISO(event.scheduled_end_date)
                    : undefined
                }
                communityId={event.community_id}
                ticketingPlatformUrl={event.ticketing_platform_url}
              />
            )}
            keyExtractor={(event, index) => `${event.id}-${index}`}
            showsVerticalScrollIndicator={false}
            numColumns={isOneColumn ? 1 : 2}
            estimatedItemSize={300}
          />
        </View>
      </View>
    );
  };

  return (
    <CacheAvailabilityProvider
      queryKeys={
        new Set([
          [communitiesKey, byIdKey],
          [communitiesKey, parentsKey, true],
        ])
      }
    >
      <FlashList
        ref={flashListRef}
        data={flashListData}
        renderItem={renderItem}
        getItemType={(item: FlashListItem) => item.type}
        keyExtractor={(item) =>
          item.type === 'header'
            ? `header-${item.sectionIndex}`
            : `section-list-${item.sectionIndex}`
        }
        refreshControl={
          onRefresh ? (
            <RefreshControl
              refreshing={false}
              onRefresh={onRefresh}
              tintColor={Colors.dark.text}
              colors={[Colors.dark.text]}
              progressBackgroundColor={Colors.dark.opacity20}
            />
          ) : undefined
        }
        ListEmptyComponent={
          isLoading ? (
            <Loader style={styles.loader} />
          ) : (
            <TextReg style={styles.emptyStateText}>{emptyStateText}</TextReg>
          )
        }
        ListFooterComponent={<View style={styles.footer} />}
        ListHeaderComponent={ListHeaderComponent}
        stickyHeaderIndices={stickyHeaderIndices}
        scrollEventThrottle={100}
        estimatedItemSize={300}
        onViewableItemsChanged={handleViewableItemsChanged}
      />
    </CacheAvailabilityProvider>
  );
};

const createStyles = ({ isMobile }: { isMobile: boolean }) =>
  StyleSheet.create({
    nestedFlashListWrapper: {
      flexDirection: 'row',
      justifyContent: 'center',
      marginHorizontal: isMobile ? 0 : CARD_MARGIN,
    },
    oneColumnListWrapper: {
      maxWidth: 600,
      flex: 1,
    },
    twoColumnListWrapper: {
      maxWidth: 1200,
      flex: 1,
    },
    cardWrapper: {
      marginHorizontal: CARD_MARGIN,
    },
    card: {
      margin: CARD_MARGIN,
      flex: 1,
    },
    emptyStateText: {
      textAlign: 'center',
      fontSize: 16,
      color: Colors.dark.text,
      opacity: 0.7,
      margin: 40,
    },
    footer: {
      height: 80,
    },
    loader: {
      marginTop: 50,
    },
  });
